import * as operations from '../../generated'
import type { z } from 'zod'
import { createDeleteOperation } from '~/services/api/createDeleteOperation'
import { createGeneratedResourceOperation } from '~/services/api/createGeneratedResourceOperation'
import { extendSchemaField } from '~/utils/extendSchemaField'
import { unboundedValueCodec } from '~/utils/unboundedValueCodec'
import { MenuCustomizationGroupStore } from '~/services/api/contracts/requests/MenuCustomizationGroupStore'
import { MenuCustomizationGroupUpdate } from '~/services/api/contracts/requests/MenuCustomizationGroupUpdate'
import type {
  MenuCustomizationGroupPage,
  MenuCustomizationGroupsSearch,
  MenuCustomizationGroupSubmission,
  MenuCustomizationGroupView,
  MenuCustomizationOptionSubmission
} from './interfaces'
import { MenuCustomizationGroupIndex } from '~/services/api/contracts/requests/MenuCustomizationGroupIndex'

const unboundedSelectionLimitExtension = {
  max_per_option: unboundedValueCodec('Infinity'),
  max_total_selections: unboundedValueCodec('Infinity')
}

const menuCustomizationGroupIndexSuccessCodecSchema = extendSchemaField(
  operations.MenuCustomizationGroupIndexSuccessSchema,
  ['data', 'items'],
  unboundedSelectionLimitExtension
)
const menuCustomizationGroupStoreSuccessCodecSchema = extendSchemaField(
  operations.MenuCustomizationGroupStoreSuccessSchema,
  ['data'],
  unboundedSelectionLimitExtension
)
const menuCustomizationGroupUpdateSuccessCodecSchema = extendSchemaField(
  operations.MenuCustomizationGroupUpdateSuccessSchema,
  ['data'],
  unboundedSelectionLimitExtension
)

type MenuCustomizationGroupResourceView = z.output<
  typeof menuCustomizationGroupIndexSuccessCodecSchema
>['data']['items'][number]

const toCustomizationGroupView = (
  resource: MenuCustomizationGroupResourceView
): MenuCustomizationGroupView => ({
  allow_multiple_selections: resource.allow_multiple_selections,
  allow_repeated_selection: resource.allow_repeated_selection,
  description: resource.description,
  id: resource.id,
  is_required: resource.is_required,
  linked_items: resource.linked_items.map((item) => ({ id: item.id, name: item.name })),
  max_per_option: resource.max_per_option,
  max_selections: resource.max_selections,
  max_total_selections: resource.max_total_selections,
  min_selections: resource.min_selections,
  name: resource.name,
  note: resource.note,
  options_count: resource.options_count,
  selection_type: operations.SelectionTypeSchema.parse(resource.selection_type),
  sort_order: resource.sort_order,
  status: resource.status,
  ...(resource.options === undefined || resource.options === null
    ? {}
    : {
        options: resource.options.map((option) => ({
          description: option.description,
          id: option.id,
          is_available: option.is_available,
          name: option.name,
          price_adjustment: option.price_adjustment,
          sort_order: option.sort_order
        }))
      })
})

const requiredMinimum = (submission: MenuCustomizationGroupSubmission) =>
  submission.is_required ? Math.max(1, submission.min_selections) : 0

const storeOption = (option: MenuCustomizationOptionSubmission) => ({
  is_available: option.is_available,
  name: option.name,
  price_adjustment: option.price_adjustment,
  ...(option.description === undefined ? {} : { description: option.description }),
  ...(option.sort_order === undefined ? {} : { sort_order: option.sort_order })
})

const updateOption = (option: MenuCustomizationOptionSubmission) => ({
  ...storeOption(option),
  ...(option.id === undefined ? {} : { id: option.id })
})

const groupRequestFields = <Option>(
  submission: MenuCustomizationGroupSubmission,
  toOption: (option: MenuCustomizationOptionSubmission) => Option
) => ({
  allow_repeated_selection: submission.allow_repeated_selection,
  max_per_option: submission.max_per_option,
  max_selections: submission.max_selections,
  max_total_selections: submission.max_total_selections,
  min_selections: requiredMinimum(submission),
  name: submission.name,
  options: submission.options.map(toOption),
  selection_type: submission.selection_type,
  ...(submission.description === undefined ? {} : { description: submission.description }),
  ...(submission.linked_items === undefined ? {} : { linked_items: submission.linked_items }),
  ...(submission.note === undefined ? {} : { note: submission.note }),
  ...(submission.status === undefined ? {} : { status: submission.status })
})

export function useMenusCustomizationGroupsService() {
  const baseURL = `${useRuntimeConfig().public.apiBaseUrl ?? ''}/api/dashboard`
  const sanctumClient = useApiRequest()
  const deleteMenuCustomizationGroup = createDeleteOperation(
    sanctumClient,
    (menuId: string | number, groupId: string | number) =>
      operations.MenuCustomizationGroupDestroyPathSchema.strict().parse({ groupId, menuId }),
    operations.MenuCustomizationGroupDestroySuccessSchema,
    ({ groupId, menuId }) => `${baseURL}/menus/${menuId}/customization-groups/${groupId}`
  )

  async function getMenuCustomizationGroupsByMenuId(
    menuId: string | number,
    query: MenuCustomizationGroupsSearch = {}
  ): Promise<MenuCustomizationGroupPage> {
    const path = operations.MenuCustomizationGroupIndexPathSchema.strict().parse({ menuId })
    const request = MenuCustomizationGroupIndex({}).parse(query)
    const response = menuCustomizationGroupIndexSuccessCodecSchema.parse(
      await sanctumClient(`${baseURL}/menus/${path.menuId}/customization-groups`, {
        globalErrorUi: true,
        query: request
      })
    )

    return {
      data: response.data.items.map((resource) => toCustomizationGroupView(resource)),
      meta: {
        count: response.data.pagination.total,
        current_page: response.data.pagination.current_page,
        has_more: response.data.pagination.current_page < response.data.pagination.last_page,
        last_page: response.data.pagination.last_page,
        per_page: response.data.pagination.per_page,
        total: response.data.pagination.total
      }
    }
  }

  const createMenuCustomizationGroup = createGeneratedResourceOperation(
    sanctumClient,
    {
      schema: operations.MenuCustomizationGroupStorePathSchema,
      parse: (menuId: string | number, _submission: MenuCustomizationGroupSubmission) =>
        operations.MenuCustomizationGroupStorePathSchema.strict().parse({ menuId }),
      buildUrl: ({ menuId }) => `${baseURL}/menus/${menuId}/customization-groups`
    },
    menuCustomizationGroupStoreSuccessCodecSchema.transform(({ data }) =>
      toCustomizationGroupView(data)
    ),
    'POST',
    {
      channel: 'body',
      schema: MenuCustomizationGroupStore({}),
      select: (_menuId, submission) => groupRequestFields(submission, storeOption)
    }
  )

  const updateMenuCustomizationGroup = createGeneratedResourceOperation(
    sanctumClient,
    {
      schema: operations.MenuCustomizationGroupUpdatePathSchema,
      parse: (
        menuId: string | number,
        groupId: string | number,
        _submission: MenuCustomizationGroupSubmission
      ) => operations.MenuCustomizationGroupUpdatePathSchema.strict().parse({ groupId, menuId }),
      buildUrl: ({ menuId, groupId }) =>
        `${baseURL}/menus/${menuId}/customization-groups/${groupId}`
    },
    menuCustomizationGroupUpdateSuccessCodecSchema.transform(({ data }) =>
      toCustomizationGroupView(data)
    ),
    'PUT',
    {
      channel: 'body',
      schema: MenuCustomizationGroupUpdate({}),
      select: (_menuId, _groupId, submission) => groupRequestFields(submission, updateOption)
    }
  )

  return {
    getMenuCustomizationGroupsByMenuId,
    createMenuCustomizationGroup,
    deleteMenuCustomizationGroup,
    updateMenuCustomizationGroup
  }
}
